import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectsApi, amendmentsApi } from '../../../api/projects';
import { ApiError } from '../../../api/client';
import { StatusBadge } from '../../../components/StatusBadge';
import { DataTable } from '../../../components/DataTable';
import { useToast } from '../../../components/Toast';
import { usePermission } from '../../../auth/AuthContext';
import { Project } from '../../../types/projects';

const amendmentSchema = z.object({
  amendmentType: z.enum(['budget_revision', 'timeline_extension', 'scope_change', 'other']),
  description: z.string().min(5, 'Describe the amendment.'),
  newBudgetTotal: z.coerce.number().optional(),
});
type AmendmentFormValues = z.infer<typeof amendmentSchema>;

/** Amendments have an independent approval lifecycle - the project's status is never affected while one is under review (tested behavior, preserved here unchanged). */
export function ProjectAmendmentsTab() {
  const { project } = useOutletContext<{ project: Project }>();
  const canAmend = usePermission('projects.amend');
  const canApprove = usePermission('projects.approve');
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const amendmentsQuery = useQuery({ queryKey: ['project-amendments', project.id], queryFn: () => projectsApi.listAmendments(project.id) });
  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm<AmendmentFormValues>({ resolver: zodResolver(amendmentSchema), defaultValues: { amendmentType: 'budget_revision' } });
  const amendmentType = watch('amendmentType');

  const createMutation = useMutation({
    mutationFn: (values: AmendmentFormValues) =>
      projectsApi.createAmendment(project.id, {
        amendmentType: values.amendmentType, description: values.description, oldValues: {},
        newValues: values.amendmentType === 'budget_revision' && values.newBudgetTotal ? { budget_total: values.newBudgetTotal } : {},
      }),
    onSuccess: () => { showSuccess('Amendment created as a draft.'); queryClient.invalidateQueries({ queryKey: ['project-amendments', project.id] }); reset(); setShowForm(false); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create amendment.'),
  });

  const submitMutation = useMutation({
    mutationFn: (amendmentId: number) => amendmentsApi.submit(amendmentId),
    onSuccess: () => { showSuccess('Amendment submitted for review.'); queryClient.invalidateQueries({ queryKey: ['project-amendments', project.id] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to submit amendment.'),
  });

  const decideMutation = useMutation({
    mutationFn: ({ amendmentId, decision }: { amendmentId: number; decision: 'approved' | 'rejected' | 'returned' }) => amendmentsApi.decide(amendmentId, { decision }),
    onSuccess: (_r, { decision }) => {
      showSuccess(decision === 'approved' ? 'Amendment approved and applied to the project.' : decision === 'returned' ? 'Amendment returned to the requester for correction.' : 'Amendment rejected.');
      queryClient.invalidateQueries({ queryKey: ['project-amendments', project.id] });
      queryClient.invalidateQueries({ queryKey: ['project', project.id] });
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to record decision.'),
  });

  return (
    <section className="card">
      <div className="page-header"><h2>Amendments</h2>{canAmend && <button onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : 'New Amendment'}</button>}</div>
      <p className="muted">Independent approval lifecycle - the project's status is not affected while an amendment is under review.</p>

      {showForm && (
        <form className="inline-form" onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
          <label>
            Type
            <select {...register('amendmentType')}>
              <option value="budget_revision">Budget Revision</option><option value="timeline_extension">Timeline Extension</option>
              <option value="scope_change">Scope Change</option><option value="other">Other</option>
            </select>
          </label>
          {amendmentType === 'budget_revision' && <label>New Budget Total<input type="number" step="0.01" {...register('newBudgetTotal')} /></label>}
          <label>Description<input {...register('description')} />{errors.description && <span className="field-error">{errors.description.message}</span>}</label>
          <button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? 'Saving…' : 'Create'}</button>
        </form>
      )}

      {amendmentsQuery.data && (
        <DataTable
          rows={amendmentsQuery.data.data}
          rowKey={(a) => a.id}
          emptyMessage="No amendments yet."
          columns={[
            { header: 'Type', render: (a) => a.amendment_type },
            { header: 'Description', render: (a) => a.description },
            { header: 'Status', render: (a) => <StatusBadge status={a.status} /> },
            {
              header: '',
              render: (a) => {
                if (canAmend && a.status === 'draft') return <button className="link-button" onClick={() => submitMutation.mutate(a.id)}>Submit</button>;
                if (canApprove && a.status === 'under_review') {
                  return (
                    <span className="row-actions">
                      <button className="link-button" onClick={() => decideMutation.mutate({ amendmentId: a.id, decision: 'approved' })}>Approve</button>
                      <button className="link-button" onClick={() => decideMutation.mutate({ amendmentId: a.id, decision: 'returned' })}>Return</button>
                      <button className="link-button" onClick={() => decideMutation.mutate({ amendmentId: a.id, decision: 'rejected' })}>Reject</button>
                    </span>
                  );
                }
                return null;
              },
            },
          ]}
        />
      )}
    </section>
  );
}
